import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Save, Bitcoin, Power, Zap, ShieldAlert, KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const COIN_ORDER = [
  "usdt_trc20",
  "usdt_bep20",
  "usdt_erc20",
  "usdc_polygon",
  "trx",
  "bnb",
  "eth",
  "ton",
];

export default function ConfigCrypto() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payoutEmail, setPayoutEmail] = useState("");
  const [payoutPassword, setPayoutPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/admin/crypto-config");
      setCfg(data);
      setPayoutEmail(""); setPayoutPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load crypto config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateCoin = (coinId, field, value) => {
    setCfg((prev) => ({
      ...prev,
      coins: {
        ...prev.coins,
        [coinId]: { ...(prev.coins?.[coinId] || {}), [field]: value },
      },
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!cfg) return;
    if (!cfg.rate_bdt_per_usd || cfg.rate_bdt_per_usd <= 0) return toast.error("Rate must be > 0");
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        rate_bdt_per_usd: Number(cfg.rate_bdt_per_usd),
        coins: cfg.coins,
        auto_withdraw_enabled: !!cfg.auto_withdraw_enabled,
        auto_withdraw_max_bdt: Number(cfg.auto_withdraw_max_bdt || 0),
      };
      if (payoutEmail.trim()) payload.payout_email = payoutEmail.trim();
      if (payoutPassword.trim()) payload.payout_password = payoutPassword.trim();
      const { data } = await api.put("/site/admin/crypto-config", payload);
      setCfg(data);
      setPayoutEmail(""); setPayoutPassword("");
      toast.success("Crypto config saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div data-testid="config-crypto-page">
      <PageHeader
        title="Crypto Configuration (NowPayments)"
        subtitle="Control which coins users can deposit / withdraw with, BDT-USD rate, and the automatic crypto withdrawal system."
      />

      <form onSubmit={save} className="space-y-5 max-w-5xl" data-testid="cfg-crypto-form">
        {/* Master toggle + rate */}
        <Section title="Status & Exchange Rate" icon={Power}>
          <label className="flex items-center justify-between p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4" data-testid="crypto-master-toggle">
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Crypto Gateway Enabled</div>
              <div className="text-[12px] text-gray-500">Master switch — when off, all crypto deposits and withdrawals are disabled site-wide.</div>
            </div>
            <input
              type="checkbox"
              checked={!!cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="h-5 w-5 cursor-pointer"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="BDT per 1 USD (Rate)" hint="Used to convert between BDT (your site currency) and USD (NowPayments currency).">
              <input
                type="number" step="0.01" min={0}
                value={cfg.rate_bdt_per_usd ?? ""}
                onChange={(e) => setCfg({ ...cfg, rate_bdt_per_usd: e.target.value })}
                className="w-full h-11 px-3 border border-gray-300 rounded-sm text-sm focus:border-emerald-500 outline-none"
                data-testid="crypto-rate-input"
              />
            </Field>
            <Field label="Status (read-only)">
              <div className="h-11 px-3 flex items-center text-sm">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold ${cfg.api_key_set ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>API Key {cfg.api_key_set ? "Set ✓" : "Missing ✗"}</span>
                <span className={`ml-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold ${cfg.ipn_secret_set ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>IPN Secret {cfg.ipn_secret_set ? "Set ✓" : "Missing ✗"}</span>
              </div>
            </Field>
          </div>
        </Section>

        {/* Per-coin controls */}
        <Section title="Coins & Networks" icon={Bitcoin}>
          <div className="text-[12px] text-gray-500 mb-3">Toggle individual coins on/off and set the minimum deposit/withdrawal in USD. The user-facing site will only show enabled coins.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Coin / Network</th>
                  <th className="text-left px-3 py-2">Display Label</th>
                  <th className="text-left px-3 py-2 w-[150px]">Min USD</th>
                  <th className="text-center px-3 py-2 w-[100px]">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {COIN_ORDER.map((cid) => {
                  const c = cfg.coins?.[cid];
                  if (!c) return null;
                  return (
                    <tr key={cid} className="border-t border-gray-100" data-testid={`coin-row-${cid}`}>
                      <td className="px-3 py-2 font-mono text-[13px] text-gray-700">
                        <div className="flex items-center gap-2">
                          <CoinDot coinId={cid} />
                          <span>{cid}</span>
                          <span className="text-[10px] uppercase text-gray-400">{c.network}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={c.label || ""}
                          onChange={(e) => updateCoin(cid, "label", e.target.value)}
                          className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-emerald-500"
                          data-testid={`coin-label-${cid}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01" min={0}
                          value={c.min_usd ?? 0}
                          onChange={(e) => updateCoin(cid, "min_usd", Number(e.target.value))}
                          className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-emerald-500"
                          data-testid={`coin-min-${cid}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!c.enabled}
                          onChange={(e) => updateCoin(cid, "enabled", e.target.checked)}
                          className="h-5 w-5 cursor-pointer"
                          data-testid={`coin-enabled-${cid}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Auto withdraw */}
        <Section title="Crypto Auto-Withdrawal" icon={Zap}>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 flex gap-2 items-start text-[12.5px] text-amber-900">
            <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <strong>Auto-withdraw uses the NowPayments Mass Payout API.</strong> When enabled, qualifying crypto withdrawals are sent on-chain instantly without admin approval. Requires NowPayments account email + password (used to obtain a JWT token at runtime; stored encrypted server-side).
            </div>
          </div>
          <label className="flex items-center justify-between p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4" data-testid="auto-withdraw-toggle">
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Enable Auto Withdraw</div>
              <div className="text-[12px] text-gray-500">When ON, eligible crypto withdrawals (≤ max amount, all 3 credentials set) bypass admin approval.</div>
            </div>
            <input
              type="checkbox"
              checked={!!cfg.auto_withdraw_enabled}
              onChange={(e) => setCfg({ ...cfg, auto_withdraw_enabled: e.target.checked })}
              className="h-5 w-5 cursor-pointer"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Max Auto-Payout Amount (BDT)" hint="Withdrawals above this amount still require manual admin approval. Set 0 to disable auto.">
              <input
                type="number" step="100" min={0}
                value={cfg.auto_withdraw_max_bdt ?? 0}
                onChange={(e) => setCfg({ ...cfg, auto_withdraw_max_bdt: Number(e.target.value) })}
                className="w-full h-11 px-3 border border-gray-300 rounded-sm text-sm outline-none focus:border-emerald-500"
                data-testid="auto-max-input"
              />
            </Field>
            <Field label="NowPayments Login Status">
              <div className="h-11 px-3 flex items-center gap-2 text-sm">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold ${cfg.payout_email_set ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>Email {cfg.payout_email_set ? "Set ✓" : "Not set"}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold ${cfg.payout_password_set ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>Password {cfg.payout_password_set ? "Set ✓" : "Not set"}</span>
              </div>
            </Field>
            <Field label="Payout Email (NowPayments login)" hint="Leave blank to keep current value.">
              <input
                type="email"
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                placeholder={cfg.payout_email_set ? "(set — leave blank to keep)" : "your-email@example.com"}
                className="w-full h-11 px-3 border border-gray-300 rounded-sm text-sm outline-none focus:border-emerald-500"
                data-testid="payout-email-input"
              />
            </Field>
            <Field label="Payout Password" hint="Leave blank to keep current value.">
              <input
                type="password"
                value={payoutPassword}
                onChange={(e) => setPayoutPassword(e.target.value)}
                placeholder={cfg.payout_password_set ? "(set — leave blank to keep)" : "Account password"}
                className="w-full h-11 px-3 border border-gray-300 rounded-sm text-sm outline-none focus:border-emerald-500"
                data-testid="payout-password-input"
              />
            </Field>
          </div>
          {cfg.auto_withdraw_enabled && !(cfg.payout_email_set && cfg.payout_password_set) && (
            <div className="mt-3 bg-rose-50 border border-rose-200 rounded-sm p-3 flex gap-2 items-start text-[12.5px] text-rose-900">
              <AlertCircle size={14} className="text-rose-600 mt-0.5 shrink-0" />
              <span>Auto-withdraw is enabled but NowPayments Email + Password are missing. Withdrawals will fall back to manual admin approval until both are configured.</span>
            </div>
          )}
        </Section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="cfg-crypto-save"
            className="inline-flex items-center gap-2 px-5 h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-sm disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Saving…" : "Save crypto config"}
          </button>
          <button type="button" onClick={load} className="px-4 h-11 border border-gray-300 rounded-sm text-sm hover:bg-gray-50">Reset</button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm">
      <div className="flex items-center gap-2 px-4 h-11 border-b border-gray-200 text-[13px] font-semibold text-gray-700">
        <Icon size={16} className="text-emerald-600" /> {title}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-gray-600 mb-1 inline-block">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-gray-400 mt-1 block">{hint}</span>}
    </label>
  );
}

function CoinDot({ coinId }) {
  const map = {
    usdt_trc20:   "#26a17b",
    usdt_bep20:   "#26a17b",
    usdt_erc20:   "#26a17b",
    usdc_polygon: "#2775ca",
    trx:          "#ef0027",
    bnb:          "#f3ba2f",
    eth:          "#627eea",
    ton:          "#0098ea",
  };
  return <span className="inline-block h-3 w-3 rounded-full" style={{ background: map[coinId] || "#888" }} />;
}
